/**
 * components/ui/index.jsx
 * Reusable primitive UI components for the settings app.
 */

export { InfoBox } from "./InfoBox";

// ── ProBadge ──────────────────────────────────────────────────────────────────
export function ProBadge() {
    return (
        <span className="inline-flex items-center rounded border border-amber-200 bg-amber-50 px-1 py-px text-[9px] font-bold uppercase tracking-wider text-amber-600">
            Pro
        </span>
    );
}

import { useState, useRef, useEffect, createPortal } from "@wordpress/element";
import { ChevronDown, Lock } from "lucide-react";
import { isPremium } from "../../utils/isPremium";

// ── Toggle ────────────────────────────────────────────────────────────────────
export function Toggle({ value, onChange, disabled = false }) {
    return (
        <button
            type="button"
            role="switch"
            dir="ltr"
            aria-checked={value}
            disabled={disabled}
            onClick={() => !disabled && onChange(!value)}
            className={[
                "relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full",
                "border-2 border-transparent outline-none transition-colors duration-200 ease-in-out",
                "focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
                value ? "bg-blue-600" : "bg-gray-200 hover:bg-gray-300",
                disabled ? "cursor-not-allowed opacity-50" : "",
            ].join(" ")}
        >
            <span
                className={[
                    "pointer-events-none inline-block h-4 w-4 transform rounded-full",
                    "bg-white shadow-sm ring-0 transition duration-200 ease-in-out",
                    value ? "translate-x-4" : "translate-x-0",
                ].join(" ")}
            />
        </button>
    );
}

// ── ButtonGroup (segmented control) ──────────────────────────────────────────
export function ButtonGroup({ options, value, onChange, className }) {
    const premium = isPremium();
    return (
        <div
            className={[
                "rounded-lg border border-gray-200 bg-gray-50 p-0.5",
                className ?? "inline-flex items-center",
            ].join(" ")}
        >
            {options.map((opt) => {
                const isLocked = opt.premium && !premium;
                const isActive = value === opt.value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => !isLocked && onChange(opt.value)}
                        title={isLocked ? "Available in Pro" : undefined}
                        className={[
                            "inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium transition-all duration-150",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
                            isLocked
                                ? isActive
                                    ? "cursor-not-allowed bg-white text-blue-400 shadow-sm"
                                    : "cursor-not-allowed text-gray-300"
                                : isActive
                                    ? "bg-white text-blue-600 shadow-sm"
                                    : "text-gray-500 hover:text-gray-800",
                        ].join(" ")}
                    >
                        {opt.label}
                        {isLocked && (
                            <Lock size={10} strokeWidth={2.5} className="shrink-0 text-amber-400" />
                        )}
                    </button>
                );
            })}
        </div>
    );
}

// ── NumberInput ───────────────────────────────────────────────────────────────
export function NumberInput({
    value,
    onChange,
    min,
    max,
    step = 1,
    unit,
    width = "w-[72px]",
}) {
    return (
        <div className="flex items-center gap-2">
            <input
                type="number"
                value={value ?? ""}
                onChange={(e) =>
                    onChange(
                        e.target.value === "" ? "" : Number(e.target.value),
                    )
                }
                min={min}
                max={max}
                step={step}
                className={[
                    width,
                    "rounded-lg border border-gray-200 bg-white px-3 py-1.5",
                    "text-sm text-gray-800 tabular-nums",
                    "transition-colors duration-150",
                    "focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30",
                    "hover:border-gray-300",
                ].join(" ")}
            />
            {unit && (
                <span className="min-w-[22px] shrink-0 text-[11px] font-medium text-gray-400">
                    {unit}
                </span>
            )}
        </div>
    );
}

// ── ColorInput helpers ────────────────────────────────────────────────────────
function parseColorValue(val) {
    if (!val) return { hex: "#000000", alpha: 1 };
    const m = val.match(
        /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/,
    );
    if (m) {
        const hex =
            "#" +
            [+m[1], +m[2], +m[3]]
                .map((n) => n.toString(16).padStart(2, "0"))
                .join("");
        return { hex, alpha: m[4] !== undefined ? parseFloat(m[4]) : 1 };
    }
    if (/^#[0-9a-fA-F]{3,8}$/.test(val)) {
        let h = val.slice(1);
        if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
        return { hex: "#" + h.slice(0, 6), alpha: 1 };
    }
    return { hex: "#000000", alpha: 1 };
}

function buildColorValue(hex, alpha) {
    if (alpha >= 1) return hex;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${Math.round(alpha * 100) / 100})`;
}

const CHECKER = {
    backgroundImage: [
        "linear-gradient(45deg,#e5e7eb 25%,transparent 25%)",
        "linear-gradient(-45deg,#e5e7eb 25%,transparent 25%)",
        "linear-gradient(45deg,transparent 75%,#e5e7eb 75%)",
        "linear-gradient(-45deg,transparent 75%,#e5e7eb 75%)",
    ].join(","),
    backgroundSize: "8px 8px",
    backgroundPosition: "0 0,0 4px,4px -4px,-4px 0",
};

// ── ColorInput ────────────────────────────────────────────────────────────────
export function ColorInput({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const [popoverPos, setPopoverPos] = useState({
        top: 0,
        left: undefined,
        right: undefined,
    });
    const swatchRef = useRef(null);
    const popoverRef = useRef(null);

    const { hex, alpha } = parseColorValue(value);
    const r = parseInt(hex.slice(1, 3), 16) || 0;
    const g = parseInt(hex.slice(3, 5), 16) || 0;
    const b = parseInt(hex.slice(5, 7), 16) || 0;
    const displayColor = alpha < 1 ? `rgba(${r},${g},${b},${alpha})` : hex;

    const getSwatchPos = (rect, top) => {
        const isRTL = document.documentElement.getAttribute("dir") === "rtl";
        return isRTL
            ? { top, right: window.innerWidth - rect.right, left: undefined }
            : { top, left: rect.left, right: undefined };
    };

    // Always open below the swatch; useEffect flips above if it overflows.
    const handleOpen = () => {
        if (swatchRef.current) {
            const rect = swatchRef.current.getBoundingClientRect();
            setPopoverPos(getSwatchPos(rect, rect.bottom + 6));
        }
        setOpen((o) => !o);
    };

    // After the popover renders, check if it overflows the viewport bottom and
    // flip it above the swatch if so. Uses the actual rendered height, not an estimate.
    useEffect(() => {
        if (!open || !popoverRef.current || !swatchRef.current) return;
        const pop = popoverRef.current.getBoundingClientRect();
        const swatch = swatchRef.current.getBoundingClientRect();
        if (pop.bottom > window.innerHeight - 8) {
            setPopoverPos(getSwatchPos(swatch, swatch.top - pop.height - 6));
        }
    }, [open]);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const close = (e) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(e.target) &&
                swatchRef.current &&
                !swatchRef.current.contains(e.target)
            )
                setOpen(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [open]);

    const handleHex = (h) => onChange(buildColorValue(h, alpha));
    const handleAlpha = (a) => onChange(buildColorValue(hex, a));

    // Portal target: the React mount element (has class wpbean-pgs-wrapper)
    // so all Tailwind scoped CSS applies correctly inside the portal.
    const portalTarget =
        document.getElementById("wpbean-pgs-setting-rule-app") || document.body;

    const popover =
        open &&
        createPortal(
            <div
                ref={popoverRef}
                style={{
                    position: "fixed",
                    top: popoverPos.top,
                    ...(popoverPos.right !== undefined
                        ? { right: popoverPos.right }
                        : { left: popoverPos.left }),
                    zIndex: 999999,
                }}
                className="w-60 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
            >
                {/* Preview strip */}
                <div className="relative h-9 w-full overflow-hidden">
                    <div className="absolute inset-0" style={CHECKER} />
                    <div
                        className="absolute inset-0"
                        style={{ backgroundColor: displayColor }}
                    />
                </div>

                <div className="space-y-4 p-4">
                    {/* Color row */}
                    <div>
                        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                            Color
                        </p>
                        <div className="flex items-center gap-2">
                            <label className="relative flex-shrink-0 cursor-pointer">
                                <input
                                    type="color"
                                    value={hex}
                                    onChange={(e) => handleHex(e.target.value)}
                                    className="sr-only"
                                />
                                <span
                                    className="block h-8 w-8 cursor-pointer rounded-lg border border-gray-200 shadow-sm transition-shadow hover:shadow-md"
                                    style={{ backgroundColor: hex }}
                                />
                            </label>
                            <input
                                type="text"
                                value={hex}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (/^#[0-9a-fA-F]{6}$/.test(v))
                                        handleHex(v);
                                }}
                                className="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 font-mono text-xs text-gray-700 transition-colors focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500/30"
                                maxLength={7}
                                spellCheck={false}
                            />
                        </div>
                    </div>

                    {/* Opacity slider */}
                    <div>
                        <div className="mb-2 flex items-center justify-between">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                                Opacity
                            </p>
                            <span className="text-[11px] font-medium tabular-nums text-gray-600">
                                {Math.round(alpha * 100)}%
                            </span>
                        </div>
                        <div className="relative h-5">
                            {/* Track */}
                            <div className="absolute inset-x-0 top-1/2 h-3 -translate-y-1/2 overflow-hidden rounded-full border border-gray-200">
                                <div
                                    className="absolute inset-0"
                                    style={CHECKER}
                                />
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background: `linear-gradient(to right,rgba(${r},${g},${b},0),rgba(${r},${g},${b},1))`,
                                    }}
                                />
                            </div>
                            {/* Thumb — always solid hex so it's visible at any alpha */}
                            <div
                                className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-white shadow-md ring-1 ring-gray-200"
                                style={{
                                    left: `${alpha * 100}%`,
                                    backgroundColor: hex,
                                }}
                            />
                            {/* Range input — fully invisible, captures drag interactions */}
                            <input
                                type="range"
                                min={0}
                                max={1}
                                step={0.01}
                                value={alpha}
                                onChange={(e) =>
                                    handleAlpha(parseFloat(e.target.value))
                                }
                                style={{
                                    position: "absolute",
                                    inset: 0,
                                    width: "100%",
                                    height: "100%",
                                    margin: 0,
                                    opacity: 0,
                                    cursor: "pointer",
                                    WebkitAppearance: "none",
                                    appearance: "none",
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>,
            portalTarget,
        );

    return (
        <div className="relative flex items-center gap-2">
            {/* Swatch trigger */}
            <button
                ref={swatchRef}
                type="button"
                onClick={handleOpen}
                className="relative h-7 w-7 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 shadow-sm transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                title={displayColor}
            >
                <span className="absolute inset-0" style={CHECKER} />
                <span
                    className="absolute inset-0"
                    style={{ backgroundColor: displayColor }}
                />
            </button>

            {/* Text value */}
            <input
                type="text"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder="#000000"
                className="w-[120px] rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 font-mono text-xs text-gray-700 tabular-nums transition-colors hover:border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30"
            />

            {popover}
        </div>
    );
}

// ── TextInput ─────────────────────────────────────────────────────────────────
export function TextInput({
    value,
    onChange,
    placeholder = "",
    width = "w-48",
}) {
    return (
        <input
            type="text"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            spellCheck={false}
            className={[
                width,
                "rounded-lg border border-gray-200 bg-white px-3 py-1.5",
                "text-sm text-gray-800 font-mono",
                "transition-colors duration-150",
                "focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30",
                "hover:border-gray-300",
            ].join(" ")}
        />
    );
}

// ── SelectInput ───────────────────────────────────────────────────────────────
export function SelectInput({ options, value, onChange }) {
    return (
        <div className="relative inline-flex items-center">
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className={[
                    "appearance-none rounded-lg border border-gray-200 bg-white",
                    "ps-3 pe-9 py-1.5",
                    "text-sm text-gray-800 cursor-pointer",
                    "transition-colors duration-150",
                    "focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30",
                    "hover:border-gray-300",
                ].join(" ")}
            >
                {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </select>
            <ChevronDown
                size={13}
                strokeWidth={2}
                className="pointer-events-none absolute end-2.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
        </div>
    );
}

// ── SettingRow ────────────────────────────────────────────────────────────────
export function SettingRow({ label, description, children, className }) {
    return (
        <div
            className={[
                className ?? "flex items-center justify-between",
                "py-[15px] [&:not(:last-child)]:border-b [&:not(:last-child)]:border-gray-100",
            ].join(" ")}
        >
            <div className="min-w-0 flex-1 pe-10">
                <p className="text-[13px] font-medium leading-snug text-gray-800">
                    {label}
                </p>
                {description && (
                    <p className="mt-0.5 text-[11.5px] leading-relaxed text-gray-400">
                        {description}
                    </p>
                )}
            </div>
            <div className="shrink-0">{children}</div>
        </div>
    );
}

// ── SectionCard ───────────────────────────────────────────────────────────────
export function SectionCard({ title, description, children }) {
    const id = title && typeof title === "string"
        ? "wcpg-s-" +
          title
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/(^-|-$)/g, "")
        : undefined;
    return (
        <div
            id={id}
            className="overflow-hidden rounded-xl border border-gray-200 bg-white transition-[box-shadow] duration-300 [&.wcpg-search-hit]:ring-2 [&.wcpg-search-hit]:ring-blue-400"
        >
            {(title || description) && (
                <div className="border-b border-gray-100 bg-gray-50/60 px-5 py-[14px]">
                    {title && (
                        <h3 className="text-[13px] font-semibold text-gray-700">
                            {title}
                        </h3>
                    )}
                    {description && (
                        <p className="mt-0.5 text-[11.5px] leading-relaxed text-gray-400">
                            {description}
                        </p>
                    )}
                </div>
            )}
            <div className="px-5">{children}</div>
        </div>
    );
}
